import { normalizeProductCategoryId } from '../domain/categoryTypes'
import type { CategoryRecord } from '../domain/categoryTypes';
import type { CategoryOption } from '../domain/categoryOptions';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { getCatalogRamSnapshotCache, type WebCatalogCategoryRow } from './catalogRamSnapshotCache';
import {
  type CategoryRow,
  mapRow,
  mapWebCategoryRow,
  resolveOrganizationId,
} from './categoriesRepoShared';
import { buildMasterWhereClause } from './masterRepoShared';
import { buildAccentInsensitiveLikeClause, matchesSearchAny } from '../../shared/format/searchText';
export type CategoryFilterState = 'all' | 'active' | 'inactive';

const filterWebCategoryRecords = (
  rows: WebCatalogCategoryRow[],
  organizationId: string | null,
  filterState: CategoryFilterState,
  search?: string
): CategoryRecord[] => {
  return rows
    .filter((row) => row?.id && (!organizationId || String(row.organization_id) === organizationId))
    .filter((row) => !row.deleted_at)
    .filter((row) => {
      if (filterState === 'active') return row.is_active !== false;
      if (filterState === 'inactive') return row.is_active === false;
      return true;
    })
    .filter((row) =>
      matchesSearchAny([row.name, row.code, row.description], search ?? ''),
    )
    .map(mapWebCategoryRow)
    .sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
};

async function categoriesFromWebCacheOrNull(
  filterState: CategoryFilterState,
  search?: string,
  limit?: number,
  offset = 0
): Promise<CategoryRecord[] | null> {
  const cached = getCatalogRamSnapshotCache();
  if (!cached?.categories.length) return null;
  const organizationId = await resolveOrganizationId();
  const filtered = filterWebCategoryRecords(cached.categories, organizationId, filterState, search);
  if (typeof limit === 'number') {
    const pageOffset = Math.max(0, offset);
    return filtered.slice(pageOffset, pageOffset + limit);
  }
  return filtered;
}

/** Nombre legible para historial / resúmenes; resuelve por id o slug e incluye categorías dadas de baja. */
export const resolveCategoryDisplayName = async (
  categoryIdOrSlug: string | null | undefined
): Promise<string> => {
  if (!categoryIdOrSlug || !String(categoryIdOrSlug).trim()) {
    return '—';
  }
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) {
    return String(categoryIdOrSlug).trim();
  }
  const raw = String(categoryIdOrSlug).trim();
  const normalized = normalizeProductCategoryId(raw);
  const cached = getCatalogRamSnapshotCache()?.categories.find((row) => {
    if (!row?.id || !organizationId || String(row.organization_id ?? '') !== organizationId) return false;
    const id = String(row.id).trim().toLowerCase();
    const slug = String(row.slug ?? '').trim().toLowerCase();
    const n = normalized.trim().toLowerCase();
    const r = raw.trim().toLowerCase();
    return id === n || slug === n || id === r || slug === r;
  });
  if (cached?.name?.trim()) return String(cached.name).trim();
  return raw;
};

/** Productos cuyo campo `category_id` coincide con el id o slug de categoría. */
export const countProductsUsingCategorySlug = async (slug: string): Promise<number> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const normalizedCategory = normalizeProductCategoryId(slug);
  const products = getCatalogRamSnapshotCache()?.products ?? [];
  return products.filter(
    (p) => normalizeProductCategoryId(String(p.category_id ?? '')) === normalizedCategory
  ).length;
};

/** Construye el fragmento de búsqueda (nombre, código, descripción) y sus parámetros. */
const buildCategorySearchClause = (search?: string): { clause: string; params: string[] } =>
  buildAccentInsensitiveLikeClause(['name', "COALESCE(code, '')", "COALESCE(description, '')"], search);

/** Contar categorías según filtro de estado */
export const countCategoriesLocal = async (
  filterState: CategoryFilterState = 'active',
  search?: string
): Promise<number> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const webCount = await categoriesFromWebCacheOrNull(filterState, search);
  return webCount !== null ? webCount.length : 0;
};

/** Listar categorías paginadas según filtro de estado */
export const listCategoriesPaged = async ({
  filterState = 'active',
  search,
  limit,
  offset = 0,
}: {
  filterState?: CategoryFilterState;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<CategoryRecord[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const webRows = await categoriesFromWebCacheOrNull(filterState, search, limit, offset);
  return webRows ?? [];
};

export const listCategories = async ({ includeInactive = true }: { includeInactive?: boolean } = {}): Promise<CategoryRecord[]> => {
  const cached = getCatalogRamSnapshotCache();
  if (cached && cached.categories.length > 0) {
    const organizationId = await resolveOrganizationId();
    return filterWebCategoryRecords(
      cached.categories,
      organizationId,
      includeInactive ? 'all' : 'active'
    );
  }
  return [];
};

export const getActiveCategoryOptions = async (): Promise<CategoryOption[]> => {
  const categories = await listCategories({ includeInactive: false });
  return categories.map((category) => ({
    id: category.id,
    label: category.name,
    description: category.description ?? null,
    color: category.color ?? null,
    appliesTo: category.appliesTo,
  }));
};
